import { useEffect, useState } from 'react';
import axios from 'axios';

// Module-level cache + listener set. The list of fuel brands rarely
// changes and is shared across many forms — fetching once and
// broadcasting updates is cheaper than each form fetching independently.
//
// The Settings → Fuel Brands page calls `refreshFuelBrands()` after
// every CRUD action so live forms pick up the change without a page
// reload.

let cached = null;
let pending = null;
const listeners = new Set();

function broadcast() {
  for (const fn of listeners) fn(cached || []);
}

function fetchOnce() {
  if (pending) return pending;
  pending = axios.get('/api/fuel-brands')
    .then((res) => {
      cached = (res.data.fuel_brands || []).map((b) => b.name);
      broadcast();
      return cached;
    })
    .catch((err) => {
      pending = null;          // allow a retry next time
      console.warn('useFuelBrands: load failed —', err.response?.data?.error || err.message);
      cached = cached || [];
      broadcast();
      return cached;
    });
  return pending;
}

// Forces a re-fetch and broadcasts the new list to every mounted hook.
// Called from the Settings page after add / edit / delete.
export function refreshFuelBrands() {
  pending = null;
  cached = null;
  return fetchOnce();
}

// Returns the current array of brand names (strings). Triggers the
// initial fetch on first call. Subsequent calls hit the cache.
export default function useFuelBrands() {
  const [brands, setBrands] = useState(cached || []);
  useEffect(() => {
    let mounted = true;
    const onUpdate = (next) => { if (mounted) setBrands(next); };
    listeners.add(onUpdate);
    if (cached) setBrands(cached);
    else        fetchOnce();
    return () => {
      mounted = false;
      listeners.delete(onUpdate);
    };
  }, []);
  return brands;
}
